
// src/components/Raporlar.js

import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, query, getDocs, orderBy, where } from 'firebase/firestore';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Grid, Stack, Tabs, Tab, TextField, InputAdornment, Autocomplete, Chip, LinearProgress } from '@mui/material';
import { Loading } from '../components/common/Loading';
import useCustomerOptions from '../hooks/useCustomerOptions';
import useCachedCollection from '../hooks/useCachedCollection';
import SearchIcon from '@mui/icons-material/Search';
import GlassCard from '../ui/glass/components/GlassCard';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { Bar, Pie } from 'react-chartjs-2';
import * as XLSX from 'xlsx';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import GiderTakibi from '../modules/expenses/GiderTakibi';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const StatCard = ({ title, value, color = "inherit" }) => (
  <GlassCard sx={{ p: 2, textAlign: 'center', height: '100%' }}>
    <Typography color="text.secondary">{title}</Typography>
    <Typography variant="h5" fontWeight="bold" sx={{ color }}>{value}</Typography>
  </GlassCard>
);

const Raporlar = () => {
  const navigate = useNavigate();
  const [tabIndex, setTabIndex] = useState(0);
  const [serviceRecords, setServiceRecords] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [sales, setSales] = useState([]); // YENİ: Satışlar
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [startDate, setStartDate] = useState(() => startOfDay(new Date()));
  const [endDate, setEndDate] = useState(() => endOfDay(new Date()));
  const [statusFilter, setStatusFilter] = useState('');
  const statuses = ["Beklemede", "İşlemde", "Tamamlandı", "Teslime Hazır", "Teslim Edildi"];
  const [selectedProductFilter, setSelectedProductFilter] = useState(null);
  const [selectedCustomerFilter, setSelectedCustomerFilter] = useState(null);

  // Müşteri ve stok listeleri tarihten bağımsız; uygulama genelindeki paylaşımlı
  // oturum cache'inden gelir (bu sayfa için ayrı okuma yapılmaz).
  const { customers } = useCustomerOptions();
  const { data: stocks } = useCachedCollection('stocks');

  // Tarih aralığı SUNUCU tarafında uygulanır.
  //
  // Önceden bu sayfa açılışta 6 koleksiyonu komple okuyor (serviceRecords 1143 +
  // transactions 2929 + collectionGroup payments 184 + sales 736 + stocks 113 +
  // customers 1324 ≈ 6400 doküman) ve tarih filtresini yalnızca client'ta
  // uyguluyordu. Varsayılan aralık "bugün" olduğu için okunan verinin neredeyse
  // tamamı anında atılıyordu.
  //
  // Tüm ilgili tarih alanlarının Firestore Timestamp olduğu doğrulandı
  // (serviceRecords/sales/transactions'ta istisnasız), bu yüzden sunucu tarafı
  // aralık sorgusu hiçbir kaydı sessizce düşürmez. Client'taki checkDateRange
  // güvenlik ağı olarak yerinde bırakıldı.
  //
  // Aralık boşsa ("Tüm Filtreleri Temizle") sorgu kısıtsız çalışır ve eskisi gibi
  // tüm koleksiyonu okur — bu artık kullanıcının bilinçli tercihi.
  useEffect(() => {
    // DatePicker'da yarım kalmış giriş "Invalid Date" üretir; böyle bir değerle
    // Firestore sorgusu hata fırlatır. Geçerli olana kadar bekle.
    const gecersiz = (d) => d instanceof Date && isNaN(d.getTime());
    if (gecersiz(startDate) || gecersiz(endDate)) return;

    let iptal = false;
    // Elle tarih yazarken her tuşta sorgu atmamak için kısa gecikme.
    const zamanlayici = setTimeout(async () => {
      setRefreshing(true);
      try {
        const araliğinBasi = startDate ? startOfDay(startDate) : null;
        const araliginSonu = endDate ? endOfDay(endDate) : null;

        const aralikKisitlari = (alan) => {
          const kisitlar = [];
          if (araliğinBasi) kisitlar.push(where(alan, '>=', araliğinBasi));
          if (araliginSonu) kisitlar.push(where(alan, '<=', araliginSonu));
          return kisitlar;
        };

        const recordsQuery = query(
          collection(db, "serviceRecords"),
          ...aralikKisitlari("olusturmaTarihi"),
          orderBy("olusturmaTarihi", "desc")
        );
        // NOT: transactions sorgusuna bilerek orderBy eklenmedi — bu veri yalnızca
        // toplanıyor, sıra önemsiz. Böylece canlıdaki mevcut (type, timestamp)
        // composite index'i aynen yeterli oluyor, yeni index gerekmiyor.
        const transactionsQuery = query(
          collection(db, "transactions"),
          where("type", "==", "tahsilat"),
          ...aralikKisitlari("timestamp")
        );
        const salesQuery = query(
          collection(db, "sales"),
          ...aralikKisitlari("olusturmaTarihi"),
          orderBy("olusturmaTarihi", "desc")
        );

        const [recordsSnapshot, transactionsSnapshot, salesSnapshot] = await Promise.all([
          getDocs(recordsQuery),
          getDocs(transactionsQuery),
          getDocs(salesQuery)
        ]);

        if (iptal) return;
        setServiceRecords(recordsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setTransactions(transactionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setSales(salesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        console.error("Rapor verileri çekilirken hata:", error);
      } finally {
        if (!iptal) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    }, 400);

    return () => { iptal = true; clearTimeout(zamanlayici); };
  }, [startDate, endDate]);

  const handleDateFilterClick = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const checkDateRange = (date) => {
    if (!date) return false;
    
    let d;
    // Firestore Timestamp ise
    if (date.toDate && typeof date.toDate === 'function') {
        d = date.toDate();
    } else if (date instanceof Date) {
        d = date;
    } else if (typeof date === 'string') {
        // Özellikle formatı bozuk string tarihler geliyorsa diye manuel parse eklendi (DD.MM.YYYY)
        if (date.includes('.')) {
            const parts = date.split('.');
            if (parts.length === 3) {
                 d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}T12:00:00Z`);
            } else {
                 d = new Date(date);
            }
        } else {
            d = new Date(date);
        }
    } else if (typeof date === 'number') {
        d = new Date(date);
    } else {
        d = new Date(date);
    }

    // Invalid date ise (bozuk tarihli geçmiş kayıt vs) tarih filtresinden GEÇEMEZ! (144.000TL hatasının çözümü)
    if (isNaN(d.getTime())) return false;

    if (startDate && d < startOfDay(startDate)) return false;
    if (endDate && d > endOfDay(endDate)) return false;
    return true;
  };

  const customerOptions = useMemo(() => {
    return customers.map(c => ({
      id: c.id,
      label: c.adSoyad || c.firmaAdi || 'İsimsiz Müşteri',
      telefon: c.telefon || '-'
    }));
  }, [customers]);

  // --- SERVİS RAPORLARI MANTIĞI ---
  const filteredRecords = useMemo(() => {
    return serviceRecords.filter(record => {
      if (!record.olusturmaTarihi) return false;
      if (statusFilter && record.durum !== statusFilter) return false;
      if (!checkDateRange(record.olusturmaTarihi)) return false;
      if (selectedCustomerFilter && record.musteriId !== selectedCustomerFilter.id) return false;
      
      if (selectedProductFilter) {
        const hasProduct = record.kullanilanParcalar?.some(item => 
          (item.aciklama || '').toLowerCase().includes(selectedProductFilter.label.toLowerCase())
        );
        if (!hasProduct) return false;
      }
      return true;
    });
  }, [serviceRecords, startDate, endDate, statusFilter, selectedCustomerFilter, selectedProductFilter]);

  const serviceReportData = useMemo(() => {
    // Sadece SERVİS cirosu
    const totalRevenue = filteredRecords.reduce((sum, record) => sum + (record.toplamTutar || 0), 0);

    // Tahsilat hesaplama: Ürün seçiliyse tahsilat yansıtılmaz (ürün bazlı tahsilat olmaz).
    let generalPaymentsTotal = 0;
    if (!selectedProductFilter) {
      const fTransactions = transactions.filter(t => {
        if (!checkDateRange(t.timestamp)) return false;
        if (selectedCustomerFilter && t.customerId !== selectedCustomerFilter.id) return false;
        return true;
      });
      generalPaymentsTotal = fTransactions.reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
    }
    
    const totalPaid = generalPaymentsTotal;

    const materialCounts = {};
    const brandCounts = {};

    filteredRecords.forEach(record => {
      const brand = record.cihazMarkaModel?.split(' ')[0];
      if (brand) { brandCounts[brand] = (brandCounts[brand] || 0) + 1; }
      if (record.kullanilanParcalar) {
        record.kullanilanParcalar.forEach(item => {
          if (item.aciklama) materialCounts[item.aciklama] = (materialCounts[item.aciklama] || 0) + 1;
        });
      }
    });

    const topMaterials = Object.entries(materialCounts).sort((a, b) => b[1] - a[1]).slice(0, 10);
    const topBrands = Object.entries(brandCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);
    return { totalRevenue, totalPaid, topMaterials, topBrands };
  }, [filteredRecords, transactions, startDate, endDate, selectedCustomerFilter, selectedProductFilter]);

  // --- STOK VE SATIŞ RAPORLARI MANTIĞI ---
  const filteredSales = useMemo(() => {
    return sales.filter(sale => {
      const durum = (sale.durum || '').toLowerCase();
      if (durum.includes('iptal') || durum.includes('taslak') || durum.includes('silin')) return false;
      if (!checkDateRange(sale.olusturmaTarihi)) return false;
      if (selectedCustomerFilter && sale.musteriId !== selectedCustomerFilter.id) return false;

      if (selectedProductFilter) {
         const hasProduct = sale.satilanUrunler?.some(item => {
           const name = item.ad || item.urunAdi || item.aciklama || '';
           return name.toLowerCase() === selectedProductFilter.label.toLowerCase();
         });
         if (!hasProduct) return false;
      }
      return true;
    });
  }, [sales, startDate, endDate, selectedCustomerFilter, selectedProductFilter]);

  const stockReportData = useMemo(() => {
    let totalSalesVolume = 0; // Ana satış toplamı
    let totalItemsSold = 0;
    const productStats = {}; // { 'Urun Adi': { count: 0, revenue: 0, brand: '', model: '' } }

    filteredSales.forEach(sale => {
      // Satışın genel toplamını öncelikle satışın ana verisinden almayı deneriz.
      // Eğer ana veride 'toplamTutar' veya 'genelToplam' varsa onu alırız (fallback).
      // Eski sistemde belki de 'toplamTutar' yok, loop içi toplanıyordu, o yüzden güvenli number parse yapıyoruz.
      let saleTotalAmount = Number(sale.toplamTutar) || Number(sale.genelToplam) || 0;
      let calculatedInnerTotal = 0;

      if (sale.satilanUrunler && Array.isArray(sale.satilanUrunler)) {
        sale.satilanUrunler.forEach(item => {
          // Sadece stoktan düşen ürünleri sayalım (isteğe göre sarf/hizmet ayrılabilir)
          // item.tip 'urun' genel kullanımıdır.
          if (item.tip === 'urun' || !item.tip || typeof item === 'object') { // Eski kayıtlarda tip olmayabilir veya objeyse genel al.
            const name = item.ad || item.urunAdi || item.aciklama || 'Bilinmeyen Ürün';

            if (!productStats[name]) {
              productStats[name] = { count: 0, revenue: 0, brand: item.marka || '-', model: item.model || '-' };
            }

            // Sayıları güvenli şekilde parse et. Virgülle girildiyse bile handle edelim.
            const rawQty = (item.miktar || 0).toString().replace(',', '.');
            const rawRev = (item.toplamFiyat || item.tutar || 0).toString().replace(',', '.');
            
            const qty = parseFloat(rawQty) || 0;
            const rev = parseFloat(rawRev) || 0;

            productStats[name].count += qty;
            productStats[name].revenue += rev;
            totalItemsSold += qty;
            calculatedInnerTotal += rev;
          }
        });
      }
      
      // Eğer veritabanında satır toplamlarıyla ana toplam tutmuyorsa (örn. indirim yapılmışsa)
      // Toplam ciroya formdan gelen ana toplamı eklemek mantıklıdır. Yoksa calculatedInnerTotal eklenir.
      if (saleTotalAmount > 0) {
        totalSalesVolume += saleTotalAmount;
      } else {
        totalSalesVolume += calculatedInnerTotal;
      }
    });

    const topProducts = Object.entries(productStats)
      .sort((a, b) => b[1].count - a[1].count) // Adede göre sırala
      .slice(0, 10);

    const detailedList = Object.entries(productStats).map(([name, stats]) => ({
      name,
      ...stats
    })).sort((a, b) => b.count - a.count);

    return { totalSalesVolume, totalItemsSold, topProducts, detailedList };
  }, [filteredSales]);

  // --- ÜRÜN LİSTESİ (Stok DB + Satışlardan gelen ürün adları birleşimi) ---
  // DİKKAT: satışlar artık seçili tarih aralığıyla sınırlı geldiği için, buradaki
  // "satışlardan gelen ürünler" kısmı da o aralıkla sınırlıdır. Stok kataloğundaki
  // ürünler her zaman listelenir; yalnızca geçmişte satılmış ama stok kaydı olmayan
  // bir ürünü aramak için tarih aralığını genişletmek gerekir.
  const productOptions = useMemo(() => {
    const productMap = new Map();

    // Stok veritabanındaki ürünler
    stocks.forEach(stock => {
      const name = stock.urunAdi || '';
      if (name && !productMap.has(name.toLowerCase())) {
        productMap.set(name.toLowerCase(), {
          label: name,
          marka: stock.marka || '-',
          stokTuru: stock.stokTuru || '-'
        });
      }
    });

    // Satışlardaki ürünleri de ekle (stokta olmayanlar için)
    sales.forEach(sale => {
      if (sale.satilanUrunler && Array.isArray(sale.satilanUrunler)) {
        sale.satilanUrunler.forEach(item => {
          const name = item.ad || item.urunAdi || item.aciklama || '';
          if (name && !productMap.has(name.toLowerCase())) {
            productMap.set(name.toLowerCase(), {
              label: name,
              marka: item.marka || '-',
              stokTuru: '-'
            });
          }
        });
      }
    });

    return Array.from(productMap.values()).sort((a, b) => a.label.localeCompare(b.label, 'tr'));
  }, [stocks, sales]);

  // --- DETAYLI SATIŞ RAPORU (TAB 3) ---
  const detailedSalesReport = useMemo(() => {
    const rows = [];
    filteredSales.forEach(sale => {
      const musteriAd = sale.musteriAdSoyad || '-';
      const satisTarihi = sale.olusturmaTarihi;
      const toplamTutar = Number(sale.toplamTutar) || 0;
      const odenenTutar = Number(sale.odenenTutar) || 0;
      let odemeDurumu = 'Ödenmedi';
      if (odenenTutar >= toplamTutar && toplamTutar > 0) odemeDurumu = 'Ödendi';
      else if (odenenTutar > 0) odemeDurumu = 'Kısmi';

      if (sale.satilanUrunler && Array.isArray(sale.satilanUrunler)) {
        sale.satilanUrunler.forEach(item => {
          const urunAd = item.ad || item.urunAdi || item.aciklama || 'Bilinmeyen Ürün';
          const marka = item.marka || '-';
          const model = item.model || '-';
          const miktar = parseFloat((item.miktar || 0).toString().replace(',', '.')) || 0;
          const birimFiyat = parseFloat((item.birimFiyat || item.fiyat || 0).toString().replace(',', '.')) || 0;
          const toplamFiyat = parseFloat((item.toplamFiyat || item.tutar || 0).toString().replace(',', '.')) || 0;

          rows.push({
            satisTarihi,
            musteriAd,
            urunAd,
            marka,
            model,
            miktar,
            birimFiyat,
            toplamFiyat,
            odemeDurumu,
            satisId: sale.id
          });
        });
      } else {
        rows.push({
          satisTarihi,
          musteriAd,
          urunAd: '(Ürün detayı yok)',
          marka: '-',
          model: '-',
          miktar: 0,
          birimFiyat: 0,
          toplamFiyat: toplamTutar,
          odemeDurumu,
          satisId: sale.id
        });
      }
    });

    // Ürün seçim filtresi
    let filtered = rows;
    if (selectedProductFilter) {
      const selectedLower = selectedProductFilter.label.toLowerCase();
      filtered = rows.filter(r => r.urunAd.toLowerCase() === selectedLower);
    }

    return filtered;
  }, [filteredSales, selectedProductFilter]);


  const handleServiceExport = () => {
    const dataToExport = filteredRecords.map(record => {
      // ... (Eski export mantığı)
      const materialsStr = (record.kullanilanParcalar || []).map(p => p.aciklama).join(', ');
      return {
        'Seri Numarası': record.cihazSeriNumarasi,
        'Müşteri': record.musteriAdSoyad,
        'Cihaz': record.cihazMarkaModel,
        'Durum': record.durum,
        'Kayıt Tarihi': record.olusturmaTarihi?.toDate().toLocaleDateString('tr-TR') || '-',
        'Toplam Tutar': record.toplamTutar,
        'Ödenen Tutar': record.odenenTutar,
        'Kalan Tutar': record.kalanTutar,
        'Kullanılan Malzemeler': materialsStr,
      };
    });
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Servis Raporu");
    XLSX.writeFile(workbook, "Servis_Raporu.xlsx");
  };

  const handleStockExport = () => {
    // 1. Sayfa: Özet Ürün Satış Raporu
    const aggregatedData = stockReportData.detailedList.map(item => ({
      'Ürün Adı': item.name,
      'Marka': item.brand,
      'Model': item.model,
      'Satılan Adet': item.count,
      'Toplam Ciro': item.revenue
    }));
    
    // 2. Sayfa: Detaylı Satış Raporu (Tab 3'teki eksiksiz veri)
    const detailedData = detailedSalesReport.map(row => {
      let tarihStr = '-';
      if (row.satisTarihi) {
        try { tarihStr = row.satisTarihi.toDate().toLocaleDateString('tr-TR'); } catch { tarihStr = '-'; }
      }
      return {
        'Satış Tarihi': tarihStr,
        'Müşteri': row.musteriAd,
        'Ürün Adı': row.urunAd,
        'Marka': row.marka,
        'Model': row.model,
        'Adet': row.miktar,
        'Birim Fiyat': row.birimFiyat,
        'Toplam Tutar': row.toplamFiyat,
        'Ödeme Durumu': row.odemeDurumu
      };
    });

    const workbook = XLSX.utils.book_new();
    
    const wsAggregated = XLSX.utils.json_to_sheet(aggregatedData);
    XLSX.utils.book_append_sheet(workbook, wsAggregated, "Özet Ürün Satış Raporu");
    
    const wsDetailed = XLSX.utils.json_to_sheet(detailedData);
    wsDetailed['!cols'] = [
      { wch: 15 }, { wch: 25 }, { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 15 }
    ];
    XLSX.utils.book_append_sheet(workbook, wsDetailed, "Detaylı Satış Kayıtları");
    
    XLSX.writeFile(workbook, "Urun_Satis_Raporu.xlsx");
  };

  const handleDetailedSalesExport = () => {
    const dataToExport = detailedSalesReport.map(row => {
      let tarihStr = '-';
      if (row.satisTarihi) {
        try { tarihStr = row.satisTarihi.toDate().toLocaleDateString('tr-TR'); } catch { tarihStr = '-'; }
      }
      return {
        'Satış Tarihi': tarihStr,
        'Müşteri': row.musteriAd,
        'Ürün Adı': row.urunAd,
        'Marka': row.marka,
        'Model': row.model,
        'Adet': row.miktar,
        'Birim Fiyat': row.birimFiyat,
        'Toplam Tutar': row.toplamFiyat,
        'Ödeme Durumu': row.odemeDurumu
      };
    });
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    worksheet['!cols'] = [
      { wch: 15 }, { wch: 25 }, { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 15 }
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Detayli Satis Raporu");
    XLSX.writeFile(workbook, "Detayli_Satis_Raporu.xlsx");
  };

  if (loading) return <Loading message="Raporlar hesaplanıyor..." />;

  const today = new Date();

  return (
    <Box sx={{ p: { xs: 1, md: 1 } }}>
      <GlassCard sx={{ mb: 2, p: 2 }}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight="bold" color="primary.main">Raporlar ve İstatistikler</Typography>
          <Typography variant="body2" color="text.secondary">Finansal durum, servis performansı ve stok hareketleri.</Typography>
        </Box>
        <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)} sx={{ mt: 2, borderBottom: 1, borderColor: 'divider' }} variant="scrollable" scrollButtons="auto">
          <Tab label="Genel Finans Kokpiti" />
          <Tab label="Stok & Ürün Satışları" />
          <Tab label="Gider Takibi" />
          <Tab label="Detaylı Satış Raporu" />
        </Tabs>
      </GlassCard>

      {/* GLOBAL FİLTRELER (0, 1 ve 3. TAB İÇİN ORTAK) */}
      {(tabIndex === 0 || tabIndex === 1 || tabIndex === 3) && (
      <GlassCard sx={{ p: 2, mb: 3 }}>
        {/* Tarih değiştiğinde veri sunucudan yeniden çekilir; sayfayı söktürmeden
            ince bir ilerleme çubuğu gösterilir. */}
        {refreshing && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 'bold' }}>Tarih Filtresi</Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
          <Button size="small" variant="outlined" onClick={() => handleDateFilterClick(startOfDay(today), endOfDay(today))}>Bugün</Button>
          <Button size="small" variant="outlined" onClick={() => handleDateFilterClick(startOfWeek(today), endOfWeek(today))}>Bu Hafta</Button>
          <Button size="small" variant="outlined" onClick={() => handleDateFilterClick(startOfMonth(today), endOfMonth(today))}>Bu Ay</Button>
          <Button size="small" variant="outlined" onClick={() => handleDateFilterClick(startOfMonth(subMonths(today, 1)), endOfMonth(subMonths(today, 1)))}>Geçen Ay</Button>
          <Button size="small" variant="outlined" onClick={() => handleDateFilterClick(startOfDay(subMonths(today, 12)), endOfDay(today))}>Son 1 Yıl</Button>
        </Stack>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}><DatePicker label="Başlangıç" value={startDate} onChange={setStartDate} slotProps={{ textField: { fullWidth: true, size: 'small' } }} /></Grid>
          <Grid item xs={12} sm={6} md={3}><DatePicker label="Bitiş" value={endDate} onChange={setEndDate} slotProps={{ textField: { fullWidth: true, size: 'small' } }} /></Grid>
          {tabIndex === 0 && (
            <Grid item xs={12} sm={6} md={3}><FormControl fullWidth size="small"><InputLabel>Servis Durumu</InputLabel><Select value={statusFilter} label="Servis Durumu" onChange={(e) => setStatusFilter(e.target.value)}><MenuItem value=""><em>Tümü</em></MenuItem>{statuses.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}</Select></FormControl></Grid>
          )}
        </Grid>

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 'bold' }}>Detaylı Veri Filtreleri</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
             <Autocomplete
                  options={customerOptions}
                  getOptionLabel={(option) => option.label}
                  value={selectedCustomerFilter}
                  onChange={(e, newValue) => setSelectedCustomerFilter(newValue)}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <li key={key} {...otherProps}>
                        <Box>
                          <Typography variant="body1" fontWeight={500}>{option.label}</Typography>
                          <Typography variant="caption" color="text.secondary">{option.telefon}</Typography>
                        </Box>
                      </li>
                    );
                  }}
                  renderInput={(params) => <TextField {...params} label="Müşteri Seçin" placeholder="Müşteri ara..." size="small" />}
                  noOptionsText="Müşteri bulunamadı"
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
                />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
                  options={productOptions}
                  getOptionLabel={(option) => option.label}
                  value={selectedProductFilter}
                  onChange={(e, newValue) => setSelectedProductFilter(newValue)}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <li key={key} {...otherProps}>
                        <Box>
                          <Typography variant="body1" fontWeight={500}>{option.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Marka: {option.marka} {option.stokTuru !== '-' ? `• Tür: ${option.stokTuru}` : ''}
                          </Typography>
                        </Box>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Ürün Seçin"
                      placeholder="Ürün ara..."
                      size="small"
                      helperText="Stok kataloğu + seçili tarih aralığındaki satışlar"
                    />
                  )}
                  noOptionsText="Ürün bulunamadı"
                  isOptionEqualToValue={(option, value) => option.label === value.label}
                  sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
                />
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
             <Button fullWidth variant="outlined" onClick={() => { setStartDate(null); setEndDate(null); setStatusFilter(''); setSelectedCustomerFilter(null); setSelectedProductFilter(null); }}>Tüm Filtreleri Temizle</Button>
          </Grid>
        </Grid>
      </GlassCard>
      )}

      {/* TAB 0: SERVİS VE GENEL FİNANS KOKPİTİ */}
      {tabIndex === 0 && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <StatCard title="Servis Cirosu" value={serviceReportData.totalRevenue.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })} color="primary.main" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StatCard title="Ürün Satış Cirosu" value={stockReportData.totalSalesVolume.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })} color="secondary.main" />
            </Grid>
          </Grid>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <StatCard title="Genel Toplam Ciro" value={(serviceReportData.totalRevenue + stockReportData.totalSalesVolume).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })} color="info.main" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard title="Genel Toplam Tahsilat" value={selectedProductFilter ? "N/A (Ürün Seçili)" : serviceReportData.totalPaid.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })} color="success.main" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard title="Net Bakiye (Alacaklar)" value={selectedProductFilter ? "N/A (Ürün Seçili)" : ((serviceReportData.totalRevenue + stockReportData.totalSalesVolume) - serviceReportData.totalPaid).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })} color="error.main" />
            </Grid>
          </Grid>


          {/* Servis Tablosu */}
          <GlassCard sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Servis Kayıtları ({filteredRecords.length})</Typography>
              <Button variant="contained" color="success" onClick={handleServiceExport} disabled={!filteredRecords.length}>Excel</Button>
            </Box>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow><TableCell>Seri No</TableCell><TableCell>Müşteri</TableCell><TableCell>Durum</TableCell><TableCell>Tarih</TableCell><TableCell align="right">Tutar</TableCell></TableRow></TableHead>
                <TableBody>{filteredRecords.map(record => (
                  <TableRow key={record.id} hover onClick={() => navigate(`/servis-detay/${record.id}`)} sx={{ cursor: 'pointer' }}>
                    <TableCell>{record.cihazSeriNumarasi}</TableCell>
                    <TableCell>{record.musteriAdSoyad}</TableCell>
                    <TableCell>{record.durum}</TableCell>
                    <TableCell>{record.olusturmaTarihi?.toDate().toLocaleDateString('tr-TR')}</TableCell>
                    <TableCell align="right">{record.toplamTutar?.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </TableContainer>
          </GlassCard>
        </Box>
      )}

      {/* TAB 1: STOK VE SATIŞ RAPORLARI */}
      {tabIndex === 1 && (
        <Box>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}><StatCard title="Toplam Ürün Satış Cirosu" value={stockReportData.totalSalesVolume.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })} color="primary.main" /></Grid>
            <Grid item xs={12} sm={4}><StatCard title="Satılan Toplam Ürün Adedi" value={stockReportData.totalItemsSold} color="info.main" /></Grid>
          </Grid>



          <GlassCard sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Detaylı Stok Kullanım / Satış Raporu</Typography>
              <Button variant="contained" color="success" onClick={handleStockExport} disabled={!stockReportData.detailedList.length}>Excel</Button>
            </Box>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Ürün Adı</TableCell>
                    <TableCell>Marka</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell align="right">Satılan Adet</TableCell>
                    <TableCell align="right">Toplam Ciro</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockReportData.detailedList.length === 0 ? (
                    <TableRow><TableCell colSpan={5} align="center">Veri bulunamadı.</TableCell></TableRow>
                  ) : (
                    stockReportData.detailedList.map((item, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>{item.brand}</TableCell>
                        <TableCell>{item.model}</TableCell>
                        <TableCell align="right"><Typography fontWeight="bold">{item.count}</Typography></TableCell>
                        <TableCell align="right">{item.revenue.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </GlassCard>
        </Box>
      )}

      {/* TAB 2: GİDER TAKİBİ */}
      {tabIndex === 2 && (
        <Box>
           <GiderTakibi />
        </Box>
      )}

      {/* TAB 3: DETAYLI SATIŞ RAPORU */}
      {tabIndex === 3 && (
        <Box>
          <GlassCard sx={{ p: 2, mb: 3 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">Detaylı Satış Raporu</Typography>
              <Typography variant="body2" color="text.secondary">
                Veritabanından ürün seçerek hangi müşteriye kaç adet, ne kadara satıldığını görün.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="contained" color="success" onClick={handleDetailedSalesExport} disabled={!detailedSalesReport.length}>Excel</Button>
            </Box>
          </GlassCard>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={3}>
              <StatCard title="Toplam Satış Sayısı" value={filteredSales.length} color="primary.main" />
            </Grid>
            <Grid item xs={12} sm={3}>
              <StatCard title={selectedProductFilter ? `"${selectedProductFilter.label}" Satılan Adet` : 'Toplam Satılan Ürün'} value={detailedSalesReport.reduce((s, r) => s + r.miktar, 0)} color="info.main" />
            </Grid>
            <Grid item xs={12} sm={3}>
              <StatCard title={selectedProductFilter ? `"${selectedProductFilter.label}" Ciro` : 'Toplam Ciro'} value={detailedSalesReport.reduce((s, r) => s + r.toplamFiyat, 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })} color="success.main" />
            </Grid>
            <Grid item xs={12} sm={3}>
              <StatCard title="Müşteri Sayısı" value={new Set(detailedSalesReport.map(r => r.musteriAd)).size} color="secondary.main" />
            </Grid>
          </Grid>

          <GlassCard sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                {selectedProductFilter ? `"${selectedProductFilter.label}" Satış Detayları` : 'Tüm Ürün Satış Detayları'}
                {' '}({detailedSalesReport.length} kayıt)
              </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Tarih</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Müşteri</TableCell>
                    {!selectedProductFilter && <TableCell sx={{ fontWeight: 600 }}>Ürün Adı</TableCell>}
                    <TableCell sx={{ fontWeight: 600 }}>Marka</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Model</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Adet</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Birim Fiyat</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Toplam Tutar</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Ödeme Durumu</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailedSalesReport.length === 0 ? (
                    <TableRow><TableCell colSpan={selectedProductFilter ? 8 : 9} align="center">
                      {selectedProductFilter ? `"${selectedProductFilter.label}" için satış kaydı bulunamadı.` : 'Yukarıdan bir ürün seçin veya tarih aralığını ayarlayın.'}
                    </TableCell></TableRow>
                  ) : (
                    detailedSalesReport.map((row, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>{row.satisTarihi ? (() => { try { return row.satisTarihi.toDate().toLocaleDateString('tr-TR'); } catch { return '-'; } })() : '-'}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{row.musteriAd}</TableCell>
                        {!selectedProductFilter && <TableCell>{row.urunAd}</TableCell>}
                        <TableCell>{row.marka}</TableCell>
                        <TableCell>{row.model}</TableCell>
                        <TableCell align="right"><Typography fontWeight="bold">{row.miktar}</Typography></TableCell>
                        <TableCell align="right">{row.birimFiyat.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{row.toplamFiyat.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell>
                        <TableCell align="center">
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: row.odemeDurumu === 'Ödendi' ? 'success.main' : row.odemeDurumu === 'Kısmi' ? 'warning.main' : 'error.main'
                            }}
                          >
                            {row.odemeDurumu}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </GlassCard>
        </Box>
      )}

    </Box>
  );
};

export default Raporlar;